import React, { useState } from "react";
import { Lock, Mail, ArrowRight, AlertCircle, X } from "lucide-react";
import { getApiBase } from "../config";

const API_BASE_URL = getApiBase();

interface LoginPageProps {
  onLogin: (token: string, role: string) => void;
}

const LoginPage = ({ onLogin }: LoginPageProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // --- Forgot password state ---
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // --- LOGIN ---
  // Hand off to the SuitPilot portal, which owns the Microsoft SSO integration.
  // `sso=1` makes the portal start the Microsoft flow immediately instead of
  // showing its own login form, so this stays a single click; `redirect` brings
  // the user back here afterwards, where the portal's .suitpilot.com session
  // cookie is adopted automatically.
  const handleMicrosoftSignIn = () => {
    // Clear any suppression left by a previous explicit logout, otherwise the
    // session would be ignored on return.
    sessionStorage.removeItem("suppress_portal_session");
    const back = `${window.location.origin}/`;
    window.location.href =
      "https://portal.suitpilot.com/login?sso=1&redirect=" +
      encodeURIComponent(back);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Login failed");

      console.log("LOGIN SUCCESSFUL. ROLE:", data.role);
      onLogin(data.access_token, data.role);
    } catch (err) {
      console.error(err);
      setError("Invalid credentials. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- FORGOT PASSWORD ---
  const sendResetEmail = async () => {
    setResetLoading(true);
    try {
      await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      setResetSent(true);
    } catch (e) {
      // intentionally silent (no user enumeration)
      setResetSent(true);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4 font-sans">
      <div className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-8 text-center border-b border-slate-700 bg-slate-800/50">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-600/20 text-blue-400 mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">iRedact Login</h2>
          <p className="text-slate-400 text-sm">
            Sign in to access your secure docket.
          </p>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-8 mt-6 flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleLogin} className="p-8 space-y-6">
          {/* Email */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Email Address
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-slate-500 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="you@firm.com"
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Password
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-slate-500 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
              />
            </div>

            {/* Forgot password */}
            <button
              type="button"
              onClick={() => {
                setResetEmail(email);
                setShowForgot(true);
                setResetSent(false);
              }}
              className="text-xs text-blue-400 hover:underline mt-1"
            >
              Forgot password?
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 transition-all disabled:opacity-70"
          >
            {isLoading ? (
              "Signing In…"
            ) : (
              <span className="inline-flex items-center gap-2">
                Sign In <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </button>

          {/* SuitPilot platform SSO.
              iRedact is not itself a relying party: we hand off to the portal,
              which owns the Microsoft integration, and come back with a session
              cookie on .suitpilot.com that this app adopts (see portalSession.ts).
              No Entra registration or redirect URI is needed for this domain. */}
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-slate-800 px-3 text-xs text-slate-500">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleMicrosoftSignIn}
            className="w-full py-3 bg-slate-900 hover:bg-slate-700 border border-slate-700 text-white font-semibold rounded-xl transition-all inline-flex items-center justify-center gap-3"
          >
            <svg className="w-4 h-4" viewBox="0 0 23 23" fill="none" aria-hidden="true">
              <path d="M11.5 0H0V11.5H11.5V0Z" fill="#F25022" />
              <path d="M23 0H11.5V11.5H23V0Z" fill="#7FBA00" />
              <path d="M11.5 11.5H0V23H11.5V11.5Z" fill="#00A4EF" />
              <path d="M23 11.5H11.5V23H23V11.5Z" fill="#FFB900" />
            </svg>
            Sign in with Microsoft
          </button>
          <p className="text-center text-xs text-slate-500">
            Uses your SuitPilot account.
          </p>
        </form>
      </div>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-2xl p-6 shadow-xl relative">
            <button
              onClick={() => setShowForgot(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-bold text-white mb-2">
              Reset password
            </h3>

            {!resetSent ? (
              <>
                <p className="text-sm text-slate-400 mb-4">
                  Enter your email and we’ll send you a reset link.
                </p>

                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full mb-4 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-white"
                  placeholder="you@firm.com"
                />

                <button
                  onClick={sendResetEmail}
                  disabled={!resetEmail || resetLoading}
                  className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl disabled:opacity-60"
                >
                  {resetLoading ? "Sending…" : "Send reset link"}
                </button>
              </>
            ) : (
              <p className="text-sm text-slate-300">
                If an account exists, you’ll receive an email shortly.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
